import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { hotList } from '@/mocks/hotList';
import { topics } from '@/mocks/topics';
import PageHeader from '@/components/feature/PageHeader';
import RankingRow from '@/pages/ranking/components/RankingRow';

const rangeOptions = [
  { key: 'today' as const, label: '今日' },
  { key: 'week' as const, label: '本周' },
  { key: 'month' as const, label: '本月' },
];

export default function Ranking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [inputValue, setInputValue] = useState(keyword);
  const [range, setRange] = useState<'today' | 'week' | 'month'>('today');
  const [category, setCategory] = useState('all');

  useSeo({
    title: '瓜圈速报热度排行 - 实时热门榜单与热议排行',
    description:
      '瓜圈速报热度排行实时更新，按今日、本周、本月维度查看娱乐热门榜单与热议排行，支持话题分类筛选与关键词搜索，快速定位当前流量焦点。',
    keywords: '瓜圈速报热度排行,瓜圈速报热门榜单,瓜圈速报热议排行,娱乐热度榜',
    path: '/ranking',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '瓜圈速报热度排行',
        description: '实时更新的娱乐热点热度排行，支持时间与话题维度筛选。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://example.com/' },
          { '@type': 'ListItem', position: 2, name: '热度排行', item: 'https://example.com/ranking' },
        ],
      },
    ],
  });

  const categories = useMemo(() => Array.from(new Set(hotList.map((item) => item.category))), []);

  const filtered = useMemo(() => {
    return hotList
      .filter((item) => {
        if (range === 'today' && item.daysAgo > 0) return false;
        if (range === 'week' && item.daysAgo > 6) return false;
        if (category !== 'all' && item.category !== category) return false;
        if (keyword) {
          const haystack = `${item.title}${item.summary}${item.category}${item.tags.join('')}`.toLowerCase();
          if (!haystack.includes(keyword.toLowerCase())) return false;
        }
        return true;
      })
      .sort((a, b) => b.heat - a.heat);
  }, [range, category, keyword]);

  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = inputValue.trim();
    if (value) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const resetFilters = () => {
    setCategory('all');
    setRange('today');
    setInputValue('');
    setSearchParams(new URLSearchParams());
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: '首页', to: '/' }, { label: '热度排行' }]}
        eyebrow="HOT RANKING"
        title="瓜圈速报热度排行"
        description="热度值由公开讨论量、搜索热度、传播速度与互动密度加权计算，每 10 分钟滚动更新。切换时间维度或话题分类，看看不同切面下的流量焦点。"
      />

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 flex flex-col gap-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-fit items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
              {rangeOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setRange(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    range === option.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex w-full items-center lg:w-auto" role="search">
              <div className="relative flex-1 lg:w-72">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  type="text"
                  placeholder="搜索标题、话题或标签"
                  aria-label="搜索热度榜"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="ml-2 h-11 cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">话题分类</span>
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                category === 'all'
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              全部
            </button>
            {categories.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setCategory(name)}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                  category === name
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-foreground-600">
                共 <span className="font-medium text-foreground-950">{filtered.length}</span> 条上榜内容
                {keyword && (
                  <span className="ml-1">
                    · 关键词「<span className="text-primary-600">{keyword}</span>」
                  </span>
                )}
              </p>
              {(keyword || category !== 'all' || range !== 'today') && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex cursor-pointer items-center gap-1 text-xs text-foreground-500 transition hover:text-primary-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              )}
            </div>

            <div className="rounded-card border border-background-200 bg-background-50 px-4 md:px-6">
              {filtered.length > 0 ? (
                <div className="divide-y divide-background-200">
                  {filtered.map((item, index) => (
                    <RankingRow key={item.id} item={item} rank={index + 1} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                    <i className="ri-emotion-unhappy-line" />
                  </span>
                  <p className="mt-4 text-sm font-medium text-foreground-900">这口瓜暂时还没上榜</p>
                  <p className="mt-1.5 text-sm text-foreground-500">换个关键词或切换时间维度再试试。</p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                  >
                    查看今日全部榜单
                  </button>
                </div>
              )}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-card border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-information-line text-lg text-primary-500" />
                榜单说明
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                <li>热度值为 0–10000 的相对分，仅代表声量，不代表事件真伪或口碑优劣。</li>
                <li>今日榜覆盖 24 小时内产生明显讨论增量的话题。</li>
                <li>涉及个人的传闻统一标注为待核实，不做真伪裁决。</li>
              </ul>
            </div>

            <div className="rounded-card border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-hashtag text-lg text-accent-600" />
                相关话题榜
              </h2>
              <ul className="mt-4 space-y-2.5">
                {topics.slice(0, 5).map((topic) => (
                  <li key={topic.id}>
                    <Link
                      to={`/topics?topic=${topic.slug}`}
                      className="group flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-sm text-foreground-600">
                          <i className={topic.icon} />
                        </span>
                        <span className="truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                          {topic.name}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line shrink-0 text-foreground-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-card bg-primary-950 p-5">
              <h2 className="font-heading text-xl tracking-wide text-background-50">想直接看结论？</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                话题榜把热点按主题归类，比榜单更适合系统性地追一条线。
              </p>
              <Link
                to="/topics"
                className="mt-4 flex h-11 items-center justify-center gap-2 rounded-md bg-accent-500 text-sm font-medium text-primary-950 transition hover:bg-accent-400"
              >
                进入话题榜
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}